import {
  getBackendExperiments,
  initializeBackendABTesting,
} from '@core/abTesting/backend/backendABTesting'
import type { BackendExperiment } from '@core/abTesting/backend/types'
import { logABTestingWarning } from '@core/abTesting/logging'
import { createApiService } from '@core/api'
import type {
  Api_WidgetConfig,
  WidgetConfigLayout,
} from '@core/api/types/api_widget_config'
import { getGlobalConfig } from '@core/globalConfig'
import { isTruthy } from '@core/helpers/boolean'
import { logWarning } from '@core/logging'
import { aliasWidgetParams } from '@core/params/helpers/aliasWidgetParams'
import type {
  EmbedFeedParams,
  IAnyWidgetParams,
  IStoryblockParams,
} from '@core/types'
import { ProductTagPlacement, ThumbnailShapeEnum } from '@core/types'
import { defaultApiHost } from '@src/helpers'

import { FWNElement } from './bases/FWNElement'

/**
 * Confluence of AB test widget performance and layout resolution
 * https://fwn.atlassian.net/wiki/spaces/WP/pages/2014609410/Widget+Performance+A+B+Tests
 */

/**
 * Layouts that render via the embed feed path (`embedFeedRender`). Used by
 * `render()` to decide whether a resolved layout should be treated as a feed.
 *
 * Intentionally excludes other `WidgetConfigLayout` values — those are handled
 * by dedicated branches in `render()` with their own entrypoints:
 *
 * - `storyblock` — `storyblockRender` (standalone storyblock sizing / title margin)
 * - `floating_player` — `storyblockRender` with `mode: 'pinned'`
 * - `player` / `horizontal_player` — `playerRender` (16:9 aspect-ratio player)
 * - `hero_unit` — `herounitRender`
 *
 * Those layouts still participate in experiment / API layout resolution via
 * `KNOWN_LAYOUTS` (and suffixes via `SUFFIX_TO_LAYOUT_MAP` when one exists);
 * they are only omitted here because they are not embed-feed widgets.
 */
const EMBED_FEED_LAYOUTS: WidgetConfigLayout[] = [
  'carousel_row',
  'carousel_grid',
  'player_deck',
  'dynamic_carousel',
  'horizontal_carousel',
  'circle_stories',
  'vertical_stories',
]

/**
 * Short-code suffixes on `widget_config_id` (e.g. `oj1w65_cs` → `cs`) mapped to
 * API layout keys. Not every `WidgetConfigLayout` has a suffix (e.g. `player`);
 * those layouts are still valid for experiment / API resolution via
 * `KNOWN_LAYOUTS`.
 *
 * Layout resolution order: known `widget_config_id` suffix, then a valid
 * experiment `variant_properties.layout`, then the config API `layout` field.
 * Suffixes support clients sensitive to Google penalization from CLS (content
 * jumping when our embed paints after the rest of the page) so the FE can
 * almost instantly populate a skeleton and reserve space. Clients without a
 * suffix use experiment layout (when present) or the API layout after config
 * fetch.
 */
const SUFFIX_TO_LAYOUT_MAP: Record<string, WidgetConfigLayout> = {
  efc: 'carousel_row',
  efg: 'carousel_grid',
  pd: 'player_deck',
  dc: 'dynamic_carousel',
  hc: 'horizontal_carousel',
  cs: 'circle_stories',
  vs: 'vertical_stories',
  sb: 'storyblock',
  fp: 'floating_player',
  hp: 'horizontal_player',
  hu: 'hero_unit',
}

/**
 * Valid `WidgetConfigLayout` values. Kept separate from `SUFFIX_TO_LAYOUT_MAP`
 * so layouts without a short-code (e.g. `player`) still pass experiment
 * `variant_properties.layout` validation.
 */
export const WIDGET_CONFIG_LAYOUTS: WidgetConfigLayout[] = [
  'carousel_row',
  'storyblock',
  'player_deck',
  'dynamic_carousel',
  'circle_stories',
  'floating_player',
  'carousel_grid',
  'hero_unit',
  'player',
  'vertical_stories',
  'horizontal_player',
  'horizontal_carousel',
]

const KNOWN_LAYOUTS: ReadonlySet<string> = new Set(WIDGET_CONFIG_LAYOUTS)

function isWidgetConfigLayout(value: string): value is WidgetConfigLayout {
  return KNOWN_LAYOUTS.has(value)
}

/**
 * Resolve layout from a known `widget_config_id` suffix. Unknown or missing
 * suffixes return null so callers can fall back to experiment / API layout.
 */
function getLayoutFromConfigId(
  widgetConfigId?: string,
): WidgetConfigLayout | null {
  const suffix = widgetConfigId?.split('_')?.[1]
  if (!suffix) {
    return null
  }
  return SUFFIX_TO_LAYOUT_MAP[suffix] ?? null
}

/**
 * Get widget experiment from backend
 */
function getWidgetExperiment(): BackendExperiment | null {
  const experiments = getBackendExperiments()
  if (!experiments) {
    return null
  }

  const widgetExperiments = experiments.filter((exp) =>
    exp.base_experiment_type.startsWith('wp_'),
  )
  if (widgetExperiments.length > 1) {
    logABTestingWarning(
      `Multiple widget experiments found: ${widgetExperiments.map((e) => e.base_experiment_type).join(', ')}. Using first match.`,
    )
  }
  return widgetExperiments[0] ?? null
}

/**
 * Resolve variant properties from experiment. Variant properties will be spread into the widget parameters.
 */
function getVariantPropertiesFromExperiment(): Record<string, string> | null {
  const experiment = getWidgetExperiment()
  if (!experiment) {
    return null
  }

  return experiment.variant_properties ?? null
}

/**
 * Layout from an AB Test experiment's variant_properties, when it is a known
 * WidgetConfigLayout. Invalid values return null so callers fall back to API layout.
 */
function getLayoutFromExperiment(): WidgetConfigLayout | null {
  const layout = getVariantPropertiesFromExperiment()?.layout
  if (!layout || !isWidgetConfigLayout(layout)) {
    return null
  }
  return layout
}

class WidgetElement extends FWNElement {
  height = '75vh'
  width = '100%'

  #businessActive = false
  #ready = false
  #widgetConfig: Api_WidgetConfig | null = null
  #configFetchFailed = false

  private getAliasedAttributes(): IAnyWidgetParams {
    return aliasWidgetParams(this.getAttributes())
  }

  /**
   * Defer render until AB test experiments are initialized and widget config is fetched.
   * `#ready` is the render gate: attribute mutations call `performRender` via
   * `FWNElement`, but those must not paint until `waitForConfigAndRender` has
   * finished (or decided the widget is disabled).
   */
  protected override performRender(): void {
    if (!this.#ready) {
      return
    }
    super.performRender()
  }

  /**
   * fw-widget initializes experiments after widget config fetch with
   * business_id/channel_id from that config, not on performRender.
   */
  protected override initializeExperiments(): void {
    // no-op
  }

  override connectedCallback(): void {
    super.connectedCallback()
    void this.waitForConfigAndRender()
  }

  private async waitForConfigAndRender(): Promise<void> {
    await this.fetchWidgetConfig()

    // Disconnect during config fetch: skip experiment init and render.
    // Distinct from the check after AB init — this one has not awaited
    // `initializeBackendABTesting` yet.
    if (!this.isConnected) {
      return
    }

    // Disabled / failed-fetch / unknown status: hide the host and stop.
    // Runs before experiment init so we do not fetch variants for a widget
    // that will never paint. `#ready` is set so later attribute changes still
    // hit `performRender` and stay no-ops via `isWidgetEnabled()`.
    if (!this.isWidgetEnabled()) {
      this.#ready = true
      this.applyStatusVisibility()
      return
    }

    // Experiment init needs ids from the fetched config, not element attrs.
    // Skip when there is no `widget_config_id` (nothing to fetch) or no
    // `business_id` (backend experiments require it). Channel id is optional.
    if (this.getAliasedAttributes().widget_config_id) {
      const businessId =
        this.#widgetConfig?.business_id || getGlobalConfig().business_id
      if (businessId) {
        await initializeBackendABTesting({
          business_id: businessId,
          channel_id:
            this.#widgetConfig?.channel_id ||
            this.#widgetConfig?.widget_properties?.channel_id,
        })
      }
    }

    // Disconnect during experiment init: skip render. Distinct from the
    // post-fetch check — the host can leave the DOM while this await is
    // in flight even if it was connected after config resolved.
    if (!this.isConnected) {
      return
    }

    this.#ready = true
    this.applyStatusVisibility()
    this.performRender()
  }

  /**
   * Hide the host when the widget must not paint: the business is inactive,
   * `status` is `disable` or an unknown value, or the config request failed.
   * Absent `status` on an active response is treated as enabled.
   */
  private applyStatusVisibility(): void {
    this.hidden = !this.isWidgetEnabled()
  }

  private isWidgetEnabled(): boolean {
    if (!this.#businessActive || this.#configFetchFailed) {
      return false
    }
    const status = this.#widgetConfig?.status
    return status === undefined || status === 'enable'
  }

  private async fetchWidgetConfig(): Promise<void> {
    const attrs = this.getAliasedAttributes()
    const widgetConfigId = attrs.widget_config_id
    if (!widgetConfigId) {
      logWarning('fw-widget requires widget_config_id to fetch layout config')
      return
    }

    try {
      const globalConfig = getGlobalConfig()
      const api = createApiService({
        apiHost: attrs.api_host || globalConfig.api_host || defaultApiHost,
        apiCache:
          attrs.api_cache !== undefined
            ? isTruthy(attrs.api_cache)
            : globalConfig.api_cache !== undefined
              ? isTruthy(globalConfig.api_cache)
              : undefined,
        apiCacheNonce: attrs.api_cache_nonce || globalConfig.api_cache_nonce,
        apiCacheHost: attrs.api_cache_host || globalConfig.api_cache_host,
      })
      const response = await api.getConfigurationsByEmbedId(widgetConfigId)
      if (response.business_active === true) {
        this.#businessActive = true
        this.#widgetConfig = response
      }
    } catch (error) {
      this.#configFetchFailed = true
      logWarning(`Error when fetching embed config params: ${error}`)
    }
  }

  /**
   * Prefer a known `widget_config_id` suffix, then experiment layout, then API `layout`.
   */
  private resolveLayout(): WidgetConfigLayout | null {
    const attrs = this.getAliasedAttributes()
    return (
      getLayoutFromConfigId(attrs.widget_config_id) ??
      getLayoutFromExperiment() ??
      this.#widgetConfig?.layout ??
      null
    )
  }

  private buildWidgetParams(layout: WidgetConfigLayout): IAnyWidgetParams {
    const attrs = this.getAliasedAttributes()
    const variantProperties = getVariantPropertiesFromExperiment()
    return {
      ...this.#widgetConfig?.widget_properties,
      ...attrs,
      ...(variantProperties ?? {}),
      layout,
    }
  }

  override render(): void {
    if (!this.isWidgetEnabled()) {
      return
    }

    const layout = this.resolveLayout()
    if (!layout) {
      return
    }

    const params = this.buildWidgetParams(layout)

    if (layout === 'storyblock') {
      const widgetParams: IStoryblockParams = {
        height: this.height,
        width: this.width,
        ...params,
      }

      const computedHeight = parseInt(getComputedStyle(this).height)
      const computedWidth = parseInt(getComputedStyle(this).width)

      this.setSpaceReservingStyle(
        'height',
        computedHeight ? getComputedStyle(this).height : this.height,
      )
      this.style.width = computedWidth
        ? getComputedStyle(this).width
        : this.width // Explicitly set width to force wrapping flex-box to respect it.

      // Add margin-bottom to storyblock to account for the instance the widget has a title
      this.setSpaceReservingStyle('margin-bottom', '100px')

      import('@embed/storyblockRender').then(({ storyblockRender }) => {
        if (this.shadowRoot) {
          storyblockRender({
            target: this.shadowRoot,
            widgetId: this.widgetId,
            widgetParams,
          })
        }
      })
    } else if (layout === 'floating_player') {
      const widgetParams: IStoryblockParams = {
        ...params,
        mode: 'pinned',
      }

      import('@embed/storyblockRender').then(({ storyblockRender }) => {
        if (this.shadowRoot) {
          storyblockRender({
            target: this.shadowRoot,
            widgetId: this.widgetId,
            widgetParams,
          })
        }
      })
    } else if (layout === 'player' || layout === 'horizontal_player') {
      import('@embed/playerRender').then(({ playerRender }) => {
        this.setSpaceReservingStyle('aspect-ratio', '16 / 9')

        if (this.shadowRoot) {
          playerRender({
            target: this.shadowRoot,
            widgetId: this.widgetId,
            widgetParams: {
              width: '100%',
              height: '100%',
              ...params,
            },
          })
        }
      })
    } else if (layout === 'hero_unit') {
      import('@embed/herounitRender').then(({ herounitRender }) => {
        if (this.shadowRoot) {
          herounitRender({
            target: this.shadowRoot,
            widgetId: this.widgetId,
            widgetParams: params,
          })
        }
      })
    } else if (EMBED_FEED_LAYOUTS.includes(layout)) {
      let widgetParams: EmbedFeedParams = params
      switch (layout) {
        case 'player_deck':
          widgetParams = {
            captions: false,
            ...params,
            player_deck_layout: true,
            product_tag_placement: ProductTagPlacement.FALSE,
          }
          break
        case 'dynamic_carousel':
          widgetParams = {
            ...params,
            thumbnail_style: 'dynamic',
          }
          break
        case 'horizontal_carousel':
          widgetParams = {
            ...params,
            thumbnail_orientation: 'horizontal',
          }
          break
        case 'circle_stories':
          widgetParams = {
            ...params,
            thumbnail_shape: ThumbnailShapeEnum.CIRCLE,
            product_tag_placement: ProductTagPlacement.FALSE,
          }
          break
        case 'vertical_stories':
          widgetParams = {
            ...params,
            thumbnail_shape: ThumbnailShapeEnum.RECTANGLE,
            product_tag_placement: ProductTagPlacement.FALSE,
          }
          break
      }

      import('@embed/embedFeedRender').then(({ embedFeedRender }) => {
        if (this.shadowRoot) {
          embedFeedRender({
            target: this.shadowRoot,
            widgetId: this.widgetId,
            widgetParams,
            mode: layout === 'carousel_grid' ? 'grid' : 'row',
          })
        }
      })
    }
  }
}

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'fw-widget': Record<string, string>
    }
  }
}

if (!customElements.get('fw-widget')) {
  customElements.define('fw-widget', WidgetElement)
}
